// 스테이지 지도 화면(DOM, S3). 마을 문 앞 카드의 「지도 열기」로 연다. 왼쪽: 지역 그림 지도 위 1-1~1-10 노드 길(깬 노드 별, 지금 노드 강조, 잠금 자물쇠,
// n-5 붉은 해골·n-10 금 왕관). 오른쪽: 고른 스테이지 카드(권장 레벨·난이도 탭·첫 클리어 보상·반복 보상·확정 드롭·스페셜 몬스터 확률·입장).
// 목표 원화: concept/stage-maps-20261023/ui-stage-select.webp(일반), ui-boss-stage-card.webp(보스). 표시 데이터는 stageMapModel(순수), 수치는 stageRewards.
// 화면 크기: 가로(844×390·568×320)는 지도 왼쪽·카드 오른쪽, 세로(390×844)는 지도 위·카드 아래. 노드 길은 최소 너비를 두고 가로로 밀어 본다.
// 글자 12px 이상, 눌리는 것 44px 이상. 영웅 입력은 게임이 막는다(village.stageMapOpen). 입장은 기존 selectStage + enterStage 경로.
import { REGION_IDS, type RegionId } from '../shared/levels';
import { Scene, type GameCommand, type GameView } from '../shared/types';
import type { DifficultyKey } from '../shared/content/special';
import { viewH, viewW } from '../shared/viewport';
import { C, FONT, el } from './dom';
import { kindLabel, mapHeader, mapNodes, mapStageId, stageCard, type IconKind, type MapNode, type MapProgress, type RewardTile, type StageCard } from './stageMapModel';
import { assetUrl } from '../pwa/assetMap';

const stageUrl = (file: string): string => assetUrl('ui/stage/' + file);
const CSS = `
.dh-sm{position:absolute;inset:0;z-index:45;display:none;flex-direction:column;background:#0a0f1e;color:#e6ecff;font-family:${FONT};overflow:hidden;pointer-events:auto;touch-action:manipulation;user-select:none;-webkit-user-select:none}
.dh-sm button{font-family:${FONT}}
.dh-sm-bg{position:absolute;inset:0;background-size:cover;background-position:center}
.dh-sm-bg::after{content:'';position:absolute;inset:0;background:linear-gradient(180deg,rgba(6,10,22,.78) 0,rgba(6,10,22,.12) 20%,rgba(6,10,22,.1) 72%,rgba(6,10,22,.55) 100%)}
.dh-sm-head{position:relative;z-index:2;display:flex;align-items:center;gap:12px;flex:0 0 auto;padding:4px 12px 4px 8px;min-height:52px;box-sizing:border-box}
.dh-sm-back{flex:0 0 auto;width:48px;height:48px;border-radius:12px;border:2px solid ${C.gold};background:rgba(16,22,42,.9);color:${C.gold};font:900 24px ${FONT};cursor:pointer;line-height:1;padding:0}
.dh-sm-name{font:900 26px ${FONT};color:#fff;letter-spacing:.5px;text-shadow:0 2px 0 #000,0 0 10px rgba(0,0,0,.8);white-space:nowrap}
.dh-sm-blurb{font:600 13px/1.35 ${FONT};color:#f3ecd2;text-shadow:0 1px 3px #000;min-width:0;flex:1 1 auto}
.dh-sm-body{position:relative;z-index:1;flex:1 1 0;min-height:0;display:flex;flex-direction:row}
.dh-sm-map{position:relative;flex:1 1 0;min-width:0;min-height:0;overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch}
.dh-sm-road{position:relative;height:100%;width:100%;min-width:860px}
.dh-sm-svg{position:absolute;inset:0;width:100%;height:100%;pointer-events:none}
.dh-sm-node{position:absolute;transform:translate(-50%,-50%);display:flex;flex-direction:column;align-items:center;gap:2px;background:none;border:0;padding:0;cursor:pointer;color:#fff;min-width:48px}
.dh-sm-disc{position:relative;width:48px;height:48px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-sizing:border-box;border:3px solid #cdbb85;background:radial-gradient(circle at 50% 35%,#4a4a40,#1a1a18);box-shadow:0 3px 8px rgba(0,0,0,.65)}
.dh-sm-node.boss .dh-sm-disc{width:60px;height:60px}
.dh-sm-node.cleared .dh-sm-disc{border-color:${C.gold};background:radial-gradient(circle at 50% 35%,#6b5420,#2a1f08);box-shadow:0 0 12px rgba(242,193,78,.7),0 3px 8px rgba(0,0,0,.65)}
.dh-sm-node.current .dh-sm-disc{border-color:#ffe07a;background:radial-gradient(circle at 50% 35%,#8a6a1c,#2b1d05);box-shadow:0 0 0 3px rgba(255,224,122,.35),0 0 22px 6px rgba(255,200,70,.85);animation:dhSmPulse 1.8s ease-in-out infinite}
.dh-sm-node.locked .dh-sm-disc{border-color:#8a8d98;background:radial-gradient(circle at 50% 35%,#4a4d58,#1e2028);opacity:.92}
.dh-sm-node.mid .dh-sm-disc{border-color:#ff5a4d;box-shadow:0 0 14px rgba(255,70,50,.7),0 3px 8px rgba(0,0,0,.65)}
.dh-sm-node.final .dh-sm-disc{border-color:#ffd24a;box-shadow:0 0 16px rgba(255,210,74,.85),0 3px 8px rgba(0,0,0,.65)}
.dh-sm-node.sel .dh-sm-disc{outline:3px solid #fff;outline-offset:3px}
.dh-sm-lab{font:900 13px ${FONT};padding:1px 9px;border-radius:999px;background:rgba(10,14,28,.86);color:#fff;white-space:nowrap;text-shadow:0 1px 0 #000}
.dh-sm-stars{font:900 13px ${FONT};line-height:1;color:${C.gold};text-shadow:0 1px 2px #000;letter-spacing:1px;min-height:13px}
.dh-sm-stars .off{color:#5b5440}
.dh-sm-card{position:relative;flex:0 0 clamp(264px,36%,400px);min-height:0;display:flex;flex-direction:column;box-sizing:border-box;margin:0 8px 8px 0;border:2px solid ${C.gold};border-radius:12px;background:linear-gradient(180deg,rgba(18,26,48,.97),rgba(9,14,30,.97));box-shadow:0 6px 22px rgba(0,0,0,.65),inset 0 0 0 3px rgba(242,193,78,.14)}
.dh-sm-cardscroll{flex:1 1 0;min-height:0;overflow-y:auto;padding:10px 12px 6px;-webkit-overflow-scrolling:touch}
.dh-sm-title{font:900 20px/1.2 ${FONT};color:#fff;text-shadow:0 2px 0 #000;word-break:keep-all}
.dh-sm-prev{display:block;width:100%;height:112px;object-fit:cover;border:2px solid #8a6f33;border-radius:6px;margin-top:8px;background:#0c1224}
.dh-sm-warn{margin-top:8px;padding:4px 8px;border:1.5px solid #b3202f;border-radius:6px;background:rgba(80,10,18,.6);color:#ff9aa4;font:900 14px ${FONT}}
.dh-sm-rage{margin-top:6px;display:flex;align-items:center;gap:8px;padding:4px 8px;border-radius:6px;font:800 13px/1.35 ${FONT};color:#ffe2d6;word-break:keep-all;border:1.5px solid #ff4a1c;background:linear-gradient(90deg,rgba(120,24,8,.65),rgba(40,8,6,.5))}
.dh-sm-rage.r2{border-color:#a040ff;color:#f0dcff;background:linear-gradient(90deg,rgba(72,18,120,.65),rgba(26,8,44,.5))}
.dh-sm-ragetag{flex:0 0 auto;padding:1px 8px;border-radius:5px;font:900 13px ${FONT};letter-spacing:1px;color:#fff;background:linear-gradient(#ffb040,#ff4a1c);text-shadow:0 1px 2px #000}
.dh-sm-rage.r2 .dh-sm-ragetag{background:linear-gradient(#ff5ae0,#a040ff)}
.dh-sm-blurb2{margin-top:8px;font:500 13px/1.4 ${FONT};color:#e6ecff;word-break:keep-all}
.dh-sm-rec{margin-top:8px;font:800 14px ${FONT};color:#ffe7a8}
.dh-sm-tabs{display:flex;gap:6px;margin-top:8px}
.dh-sm-tab{flex:1 1 0;min-height:44px;border-radius:8px;border:2px solid #56506a;background:rgba(20,26,46,.9);color:#9aa0b8;font:900 15px ${FONT};cursor:pointer;display:flex;align-items:center;justify-content:center;gap:4px;padding:0 4px}
.dh-sm-tab.on{border-color:${C.gold};background:linear-gradient(#5a4516,#2b210a);color:#fff;box-shadow:0 0 8px rgba(242,193,78,.5)}
.dh-sm-tab.on.hell{border-color:#ff5a4a;background:linear-gradient(#6a1410,#2a0806);box-shadow:0 0 8px rgba(255,90,74,.55)}
.dh-sm-lockhint{margin-top:4px;font:700 12px/1.3 ${FONT};color:#ff9a8a;word-break:keep-all}
.dh-sm-tab[disabled]{cursor:not-allowed;opacity:.75}
.dh-sm-sec{margin-top:8px;padding:6px 8px 8px;border:1.5px solid rgba(242,193,78,.35);border-radius:8px;background:rgba(0,0,0,.28)}
.dh-sm-sech{font:900 13px ${FONT};color:#ffe7a8;margin-bottom:4px}
.dh-sm-tiles{display:flex;flex-wrap:wrap;gap:6px}
.dh-sm-tile{display:flex;flex-direction:column;align-items:center;gap:2px;min-width:60px;flex:1 1 60px;max-width:110px}
.dh-sm-ic{width:44px;height:44px;border-radius:8px;display:flex;align-items:center;justify-content:center;border:1.5px solid #6a7292;background:radial-gradient(circle at 50% 30%,#2d3658,#121a30);font:900 20px ${FONT};line-height:1}
.dh-sm-ic img{width:34px;height:34px;object-fit:contain}
.dh-sm-ic.gold{border-color:${C.gold}}.dh-sm-ic.exp{border-color:#46c46a;background:radial-gradient(circle at 50% 30%,#2f7a46,#10301c);color:#e8ffe8;font-size:13px}
.dh-sm-ic.stone{border-color:#7fd8ff;color:#aeeaff}.dh-sm-ic.gear{border-color:#4a8cff;color:#b8d2ff}.dh-sm-ic.set{border-color:#3fd07a;color:#a8ffc8}
.dh-sm-ic.gem{border-color:#a45cff;color:#d8b8ff}.dh-sm-ic.legend{border-color:#f2b233;color:#ffdc86}.dh-sm-ic.chest{border-color:${C.gold};color:#ffe7a8}
.dh-sm-tx{font:800 13px ${FONT};color:#fff;text-align:center;white-space:nowrap}
.dh-sm-line{margin-top:8px;font:800 14px ${FONT};color:#fff;display:flex;gap:8px;align-items:center}
.dh-sm-line b{color:#ffe07a;font:900 15px ${FONT}}
.dh-sm-muted{color:#9aa0b8;font:600 13px ${FONT}}
.dh-sm-foot{flex:0 0 auto;padding:6px 12px 10px}
.dh-sm-cardscroll.more{-webkit-mask-image:linear-gradient(#000 calc(100% - 22px),transparent);mask-image:linear-gradient(#000 calc(100% - 22px),transparent)}
.dh-sm-go{width:100%;min-height:52px;border-radius:12px;border:2px solid ${C.gold};background:linear-gradient(#ffd877,#e0a52f);color:#2a1d05;font:900 24px ${FONT};letter-spacing:2px;cursor:pointer;box-shadow:0 4px 0 rgba(0,0,0,.45)}
.dh-sm-go.boss{border-color:#ff8a7a;background:linear-gradient(#e0483c,#8c1420);color:#fff}
.dh-sm-go[disabled]{cursor:not-allowed;background:linear-gradient(#59606f,#363b47);border-color:#8a8d98;color:#c8cbd6;box-shadow:none}
.dh-sm.port .dh-sm-body{flex-direction:column}
.dh-sm.port .dh-sm-map{flex:0 0 34%}
.dh-sm.port .dh-sm-prev{height:84px;margin-top:6px}
.dh-sm.port .dh-sm-card{flex:1 1 0;margin:0 8px 8px}
.dh-sm.port .dh-sm-blurb{display:none}
.dh-sm.port .dh-sm-name{font-size:22px}
.dh-sm.tiny .dh-sm-head{min-height:44px;padding-top:0;padding-bottom:0}
.dh-sm.tiny .dh-sm-back{width:44px;height:44px}
.dh-sm.tiny .dh-sm-name{font-size:19px}
.dh-sm.tiny .dh-sm-blurb,.dh-sm.tiny .dh-sm-prev,.dh-sm.tiny .dh-sm-blurb2{display:none}
.dh-sm.tiny .dh-sm-title{font-size:17px}
.dh-sm.tiny .dh-sm-go{min-height:46px;font-size:20px}
.dh-sm.tiny .dh-sm-foot{padding:4px 10px 8px}
.dh-sm.tiny .dh-sm-card{flex-basis:clamp(240px,46%,340px)}
.dh-sm.tiny .dh-sm-cardscroll{padding:6px 10px 4px}
.dh-sm.tiny .dh-sm-warn{margin-top:4px;padding:1px 8px;font-size:13px}
.dh-sm.tiny .dh-sm-rec{margin-top:4px;font-size:13px}
.dh-sm.tiny .dh-sm-tabs{margin-top:5px;gap:4px}
.dh-sm.tiny .dh-sm-tab{font-size:14px}
.dh-sm.tiny .dh-sm-sec{margin-top:5px;padding:4px 6px 5px}
.dh-sm.tiny .dh-sm-sech{margin-bottom:2px}
.dh-sm.tiny .dh-sm-ic{width:38px;height:38px;font-size:17px}
.dh-sm.tiny .dh-sm-ic img{width:28px;height:28px}
.dh-sm.tiny .dh-sm-tile{min-width:52px;flex-basis:52px;gap:1px}
.dh-sm.tiny .dh-sm-line{margin-top:5px}
.dh-sm.tiny .dh-sm-road{min-width:760px}
@keyframes dhSmPulse{0%,100%{box-shadow:0 0 0 3px rgba(255,224,122,.35),0 0 18px 4px rgba(255,200,70,.7)}50%{box-shadow:0 0 0 5px rgba(255,224,122,.5),0 0 28px 9px rgba(255,200,70,.95)}}
@media(prefers-reduced-motion:reduce){.dh-sm-node.current .dh-sm-disc{animation:none}}
`;

let cssDone = false;
/** 난이도별 선택 명령 꼬리표(하드 {hard}, 헬 {hell}) */
function diffFlags(diff: DifficultyKey): { hard?: true; hell?: true } {
  return diff === 'hell' ? { hell: true } : diff === 'hard' ? { hard: true } : {};
}

function installCss(): void {
  if (cssDone || typeof document === 'undefined') return;
  cssDone = true;
  const st = document.createElement('style'); st.id = 'dh-stagemap-style'; st.textContent = CSS; document.head.appendChild(st);
}

const NS = 'http://www.w3.org/2000/svg';
function svgEl(tag: string, attrs: Record<string, string>, parent?: Element): SVGElement {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  if (parent) parent.appendChild(e);
  return e as SVGElement;
}
const ICON_GLYPH: Partial<Record<IconKind, string>> = { stone: '◆', gear: '⚔', gem: '◈', set: '✦', legend: '★', chest: '▣', town: '⌂' };

/** 노드 안 그림(자물쇠·해골·왕관·교차 검·체크) — 글꼴에 없는 기호가 깨지지 않게 SVG로 그린다 */
function nodeIcon(n: MapNode): SVGElement {
  const s = svgEl('svg', { viewBox: '0 0 24 24', width: n.kind === 'normal' ? '26' : '34', height: n.kind === 'normal' ? '26' : '34', 'aria-hidden': 'true' });
  const f = (c: string) => ({ fill: c, stroke: '#1a1408', 'stroke-width': '0.8', 'stroke-linejoin': 'round' });
  if (n.state === 'locked') {
    svgEl('path', { d: 'M7 11V8a5 5 0 0 1 10 0v3', fill: 'none', stroke: '#d4d6de', 'stroke-width': '2.4', 'stroke-linecap': 'round' }, s);
    svgEl('rect', { x: '5', y: '11', width: '14', height: '10', rx: '2', ...f('#d4d6de') }, s);
    svgEl('circle', { cx: '12', cy: '16', r: '1.6', fill: '#3a3d48' }, s);
    return s;
  }
  if (n.kind === 'mid') {
    svgEl('path', { d: 'M12 3C7 3 4 6.5 4 10.5c0 2.4 1.2 4 2.6 5V19h10.8v-3.5c1.4-1 2.6-2.6 2.6-5C20 6.5 17 3 12 3Z', ...f('#f2e8d0') }, s);
    svgEl('circle', { cx: '9', cy: '10.5', r: '2', fill: '#b3202f' }, s); svgEl('circle', { cx: '15', cy: '10.5', r: '2', fill: '#b3202f' }, s);
    svgEl('path', { d: 'M10 15.5v2M12 15.5v2M14 15.5v2', stroke: '#5a4a38', 'stroke-width': '1.2' }, s);
    return s;
  }
  if (n.kind === 'final') {
    svgEl('path', { d: 'M3 18 2 7l5 4 5-7 5 7 5-4-1 11Z', ...f('#ffd24a') }, s);
    svgEl('rect', { x: '3', y: '18', width: '18', height: '3', rx: '1', ...f('#e0a52f') }, s);
    return s;
  }
  if (n.state === 'cleared') { svgEl('path', { d: 'M5 12.5l4.5 4.5L19 7', fill: 'none', stroke: '#ffe07a', 'stroke-width': '3.2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, s); return s; }
  // 열림·지금: 교차 검
  const c = n.state === 'current' ? '#fff3c4' : '#d9d3bd';
  svgEl('path', { d: 'M4 4l10 10M20 4 10 14', stroke: c, 'stroke-width': '2.6', 'stroke-linecap': 'round' }, s);
  svgEl('path', { d: 'M3 15l6 6M21 15l-6 6M12 12l-2 2M12 12l2 2', stroke: c, 'stroke-width': '2.2', 'stroke-linecap': 'round' }, s);
  return s;
}

/** 노드 좌표 → 부드러운 길(Catmull-Rom → 베지어). 좌표는 0..100 */
function smoothPath(pts: readonly (readonly [number, number])[]): string {
  if (pts.length < 2) return '';
  let d = `M${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6], c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C${c1[0].toFixed(2)} ${c1[1].toFixed(2)} ${c2[0].toFixed(2)} ${c2[1].toFixed(2)} ${p2[0]} ${p2[1]}`;
  }
  return d;
}

export class StageMapUi {
  private readonly root: HTMLDivElement;
  private readonly bg: HTMLDivElement;
  private readonly name: HTMLDivElement;
  private readonly blurb: HTMLDivElement;
  private readonly map: HTMLDivElement;
  private readonly road: HTMLDivElement;
  private readonly cardScroll: HTMLDivElement;
  private readonly foot: HTMLDivElement;
  private region: RegionId = 'r1';
  private sel = 1;
  private shown = false;
  private key = '';
  private wasOpen = false;
  private card: StageCard | null = null;

  constructor(parent: HTMLElement, private readonly view: GameView, private readonly send: (c: GameCommand) => void) {
    installCss();
    this.root = el('div', '', parent);
    this.root.className = 'dh-sm';
    this.root.dataset.testid = 'stage-map';
    this.bg = el('div', '', this.root); this.bg.className = 'dh-sm-bg';
    const head = el('div', '', this.root); head.className = 'dh-sm-head';
    const back = el('button', '', head, '◀'); back.className = 'dh-sm-back'; back.type = 'button'; back.dataset.testid = 'stage-map-back';
    back.setAttribute('aria-label', '지도 닫기');
    back.addEventListener('click', () => this.close());
    this.name = el('div', '', head); this.name.className = 'dh-sm-name';
    this.blurb = el('div', '', head); this.blurb.className = 'dh-sm-blurb';
    const body = el('div', '', this.root); body.className = 'dh-sm-body';
    this.map = el('div', '', body); this.map.className = 'dh-sm-map'; this.map.dataset.testid = 'stage-map-road';
    this.road = el('div', '', this.map); this.road.className = 'dh-sm-road';
    const card = el('div', '', body); card.className = 'dh-sm-card'; card.dataset.testid = 'stage-map-card';
    this.cardScroll = el('div', '', card); this.cardScroll.className = 'dh-sm-cardscroll';
    this.foot = el('div', '', card); this.foot.className = 'dh-sm-foot';
    this.cardScroll.addEventListener('scroll', () => this.updateMore(), { passive: true });
    for (const t of ['pointerdown', 'pointerup', 'touchstart', 'wheel']) this.root.addEventListener(t, (e) => e.stopPropagation(), { passive: true });
  }

  /**
   * 지도가 지금 보여 줄 난이도와 그 진행 소스. 문 앞 카드 난이도 버튼과 같은 소스: 헬 1지역이 열렸고 그 지역 헬이 열렸고 문 난이도가 헬이면 헬,
   * 아니면 하드(같은 조건), 아니면 노말. 문은 헬 → 하드 → 노말 순서로 본다(game.doorStage)
   */
  private mode(): { diff: DifficultyKey; hardOpen: boolean; hellOpen: boolean; src: MapProgress } {
    const h = this.view.hard, x = this.view.hell;
    const hardOpen = !!h && h.available && h.regionUnlocked(this.region);
    const hellOpen = !!x && x.available && x.regionUnlocked(this.region);
    if (hellOpen && x!.on) return { diff: 'hell', hardOpen, hellOpen, src: x! };
    if (hardOpen && h!.on) return { diff: 'hard', hardOpen, hellOpen, src: h! };
    return { diff: 'normal', hardOpen, hellOpen, src: this.view.progress };
  }

  /** 문 앞 카드가 어느 지역의 지도를 열지 정한다(openStageMap 명령 전에 부른다). 지금 들어갈 스테이지를 고른 상태로 시작 */
  prepare(region: RegionId): void {
    this.region = region;
    const sel = this.mode().src.selected(region);
    this.sel = sel ? Number(sel.slice(-2)) : 1;
    this.key = '';
  }

  get visible(): boolean { return this.shown; }

  private close(): void { this.send({ type: 'closeMenu' }); }

  private choose(index: number): void {
    this.sel = index;
    this.key = '';
    const m = this.mode();
    if (m.src.unlocked(mapStageId(REGION_IDS.indexOf(this.region) + 1, index, m.diff))) this.send({ type: 'selectStage', region: this.region, index, ...diffFlags(m.diff) });
  }

  /** 난이도 탭: 문 난이도(setDifficulty)를 바꾸고, 바뀐 난이도에서 들어갈 스테이지를 고른 상태로 맞춘다 */
  private chooseDiff(key: DifficultyKey): void {
    const m = this.mode();
    if (key === m.diff || (key === 'hard' && !m.hardOpen) || (key === 'hell' && !m.hellOpen)) return;
    this.send({ type: 'setDifficulty', hard: key !== 'normal', ...(key === 'hell' ? { hell: true } : {}) });
    const h = key === 'hell' ? this.view.hell : key === 'hard' ? this.view.hard : undefined;
    const sel = (h ?? this.view.progress).selected(this.region);
    this.sel = sel ? Number(sel.slice(-2)) : 1;
    this.key = '';
  }

  private enter(): void {
    const c = this.card;
    if (!c || c.locked) return;
    this.send({ type: 'selectStage', region: this.region, index: c.index, ...diffFlags(c.diff) });
    this.send({ type: 'enterStage', stage: c.id });
  }

  update(): void {
    const v = this.view;
    const on = v.flow.scene === Scene.Village && v.village.stageMapOpen === true;
    if (!on) {
      if (this.shown) { this.shown = false; this.root.style.display = 'none'; }
      this.wasOpen = false;
      return;
    }
    const W = viewW(), H = viewH();
    const port = W < H, tiny = !port && H <= 420 || port && W <= 360;
    const pr = v.progress, hd = this.mode();
    const key = [this.region, this.sel, pr.revision, pr.cleared, hd.diff, hd.hardOpen ? 1 : 0, hd.hellOpen ? 1 : 0, v.hard ? v.hard.revision : 0, v.hell ? v.hell.revision : 0, port ? 'p' : 'l', tiny ? 't' : 'n'].join('|');
    if (!this.shown) { this.shown = true; this.root.style.display = 'flex'; }
    if (key === this.key) return;
    this.key = key;
    this.root.classList.toggle('port', port);
    this.root.classList.toggle('tiny', tiny);
    this.render();
    if (!this.wasOpen) { this.wasOpen = true; this.scrollToSel(); }
    this.updateMore();
  }

  /** 카드가 아래에 더 있으면 아래 가장자리를 흐리게 해 스크롤할 수 있음을 알린다 */
  private updateMore(): void {
    const s = this.cardScroll;
    s.classList.toggle('more', s.scrollHeight - s.clientHeight - s.scrollTop > 3);
  }

  private scrollToSel(): void {
    const n = this.road.querySelector('.dh-sm-node.sel') as HTMLElement | null;
    if (!n) return;
    this.map.scrollLeft = Math.max(0, n.offsetLeft - this.map.clientWidth / 2);
  }

  private render(): void {
    const { diff, hardOpen, hellOpen, src: pr } = this.mode();
    const no = REGION_IDS.indexOf(this.region) + 1;
    const head = mapHeader(this.region);
    this.name.textContent = head.name;
    this.blurb.textContent = head.blurb;
    this.bg.style.backgroundImage = `url(${stageUrl(this.region + '-a.webp')})`;
    // 노드 길
    const nodes = mapNodes(pr, this.region, this.sel, diff);
    this.road.textContent = '';
    const svg = svgEl('svg', { viewBox: '0 0 100 100', preserveAspectRatio: 'none' }) as SVGSVGElement;
    svg.setAttribute('class', 'dh-sm-svg');
    this.road.appendChild(svg);
    const pts = nodes.map((n) => [n.x * 100, n.y * 100] as const);
    svgEl('path', { d: smoothPath(pts), fill: 'none', stroke: 'rgba(8,12,24,.7)', 'stroke-width': '9', 'stroke-linecap': 'round', 'vector-effect': 'non-scaling-stroke' }, svg);
    svgEl('path', { d: smoothPath(pts), fill: 'none', stroke: '#e8dcb0', 'stroke-width': '4', 'stroke-linecap': 'round', 'stroke-dasharray': '2 9', 'vector-effect': 'non-scaling-stroke' }, svg);
    let reach = 0;
    nodes.forEach((n, i) => { if (n.state === 'cleared' || n.state === 'current') reach = i; });
    if (reach > 0) svgEl('path', { d: smoothPath(pts.slice(0, reach + 1)), fill: 'none', stroke: '#ffd877', 'stroke-width': '5', 'stroke-linecap': 'round', 'vector-effect': 'non-scaling-stroke' }, svg);
    for (const n of nodes) this.road.appendChild(this.nodeEl(n));
    // 카드
    this.card = stageCard(pr, this.region, this.sel, diff, hardOpen, hellOpen);
    this.drawCard(this.card, no);
  }

  private nodeEl(n: MapNode): HTMLButtonElement {
    const b = el('button', `left:${(n.x * 100).toFixed(2)}%;top:${(n.y * 100).toFixed(2)}%;`, undefined);
    b.type = 'button';
    b.className = `dh-sm-node ${n.state} ${n.kind === 'normal' ? '' : 'boss ' + n.kind}${n.selected ? ' sel' : ''}`;
    b.dataset.testid = 'stage-node-' + n.index;
    b.dataset.state = n.state;
    b.setAttribute('aria-label', `${n.label} ${kindLabel(n.kind)}${n.state === 'locked' ? ' 잠김' : n.state === 'current' ? ' 지금 도전' : n.state === 'cleared' ? ' 클리어' : ''}`);
    const disc = el('div', '', b); disc.className = 'dh-sm-disc';
    disc.appendChild(nodeIcon(n) as unknown as Node);
    const lab = el('div', '', b, n.label); lab.className = 'dh-sm-lab';
    const stars = el('div', '', b); stars.className = 'dh-sm-stars';
    if (n.state === 'cleared' && n.stars >= 0) {
      for (let i = 0; i < 3; i++) { const s = el('span', '', stars, '★'); if (i >= n.stars) s.className = 'off'; }
      stars.setAttribute('aria-label', '별 ' + n.stars + '개');
    }
    b.addEventListener('click', (e) => { e.stopPropagation(); this.choose(n.index); });
    return b;
  }

  private tile(parent: HTMLElement, t: RewardTile): void {
    const w = el('div', '', parent); w.className = 'dh-sm-tile'; w.title = t.title;
    const ic = el('div', '', w); ic.className = 'dh-sm-ic ' + t.icon;
    if (t.icon === 'gold') { const img = el('img', '', ic); img.src = assetUrl('ui/rewards/gold-coins.webp'); img.alt = ''; img.draggable = false; }
    else if (t.icon === 'exp') ic.textContent = 'EXP';
    else ic.textContent = ICON_GLYPH[t.icon] ?? '◆';
    const tx = el('div', '', w, t.text); tx.className = 'dh-sm-tx';
  }

  private section(parent: HTMLElement, title: string, tiles: readonly RewardTile[], testid: string): void {
    const s = el('div', '', parent); s.className = 'dh-sm-sec'; s.dataset.testid = testid;
    const h = el('div', '', s, '✦ ' + title); h.className = 'dh-sm-sech';
    const row = el('div', '', s); row.className = 'dh-sm-tiles';
    for (const t of tiles) this.tile(row, t);
  }

  private drawCard(c: StageCard, no: number): void {
    const p = this.cardScroll;
    p.textContent = '';
    const title = el('div', '', p, c.title); title.className = 'dh-sm-title'; title.dataset.testid = 'stage-card-title';
    const img = el('img', '', p); img.className = 'dh-sm-prev'; img.alt = '';
    img.src = stageUrl(`${no === 1 && c.kind === 'mid' ? 'r1-m' : no === 1 && c.kind === 'final' ? 'r1-f' : this.region + '-a'}.webp`);
    if (c.warning) { const w = el('div', '', p, '! ' + c.warning); w.className = 'dh-sm-warn'; }
    if (c.rage.kind) {
      // S5 분노·광폭: 이름 표 + 문턱·효과 한 줄(경고 줄 바로 아래)
      const r = el('div', '', p); r.className = 'dh-sm-rage r' + c.rage.kind; r.dataset.testid = 'stage-card-rage';
      const tag = el('span', '', r, c.rage.name); tag.className = 'dh-sm-ragetag';
      el('span', '', r, c.rage.text);
    }
    const bl = el('div', '', p, c.blurb); bl.className = 'dh-sm-blurb2';
    const rec = el('div', '', p, '⚔ 권장 Lv. ' + c.recLevel + (c.cleared ? ' · 클리어 ' + c.clears + '회' : '')); rec.className = 'dh-sm-rec'; rec.dataset.testid = 'stage-card-rec';
    const tabs = el('div', '', p); tabs.className = 'dh-sm-tabs';
    for (const t of c.tabs) {
      const b = el('button', '', tabs, t.label); b.type = 'button';
      b.className = 'dh-sm-tab' + (t.active ? ' on' : '') + (t.key === 'hell' ? ' hell' : '');
      b.dataset.testid = 'stage-tab-' + t.key;
      // 이미 고른 난이도를 다시 눌러도 눌림이 보이게 짧게 강조한다(RG-011: 선택 상태라 화면이 안 바뀌는 것이 무반응으로 보였다)
      if (!t.locked) b.addEventListener('click', (e) => {
        e.stopPropagation();
        if (t.active) b.animate?.([{ transform: 'scale(.94)' }, { transform: 'scale(1.04)' }, { transform: 'scale(1)' }], { duration: 220, easing: 'ease-out' });
        this.chooseDiff(t.key);
      });
      if (t.locked) {
        b.disabled = true; b.title = t.hint ?? '아직 열리지 않았다'; b.setAttribute('aria-label', t.label + ' 잠김' + (t.hint ? ' · ' + t.hint : ''));
        const lk = svgEl('svg', { viewBox: '0 0 24 24', width: '15', height: '15', 'aria-hidden': 'true' });
        svgEl('path', { d: 'M7 11V8a5 5 0 0 1 10 0v3', fill: 'none', stroke: '#c8cbd6', 'stroke-width': '2.6', 'stroke-linecap': 'round' }, lk);
        svgEl('rect', { x: '5', y: '11', width: '14', height: '10', rx: '2', fill: '#c8cbd6' }, lk);
        b.appendChild(lk as unknown as Node);
      }
    }
    const lockedHint = c.tabs.find((t) => t.locked && t.hint);
    if (lockedHint) { const lh = el('div', '', p, lockedHint.label + ' 잠김 · ' + lockedHint.hint); lh.className = 'dh-sm-lockhint'; lh.dataset.testid = 'stage-tab-hint'; }
    if (c.first.length) this.section(p, '첫 클리어 보상', c.first, 'stage-card-first');
    else if (c.firstNote) { const m = el('div', '', p, '✦ 첫 클리어 보상 · ' + c.firstNote); m.className = 'dh-sm-line dh-sm-muted'; m.dataset.testid = 'stage-card-first'; }
    this.section(p, '반복 보상', c.repeat, 'stage-card-repeat');
    if (c.drops.length) this.section(p, '확정 드롭', c.drops, 'stage-card-drops');
    if (c.special) { const sp = el('div', '', p); sp.className = 'dh-sm-line'; sp.dataset.testid = 'stage-card-special'; el('span', '', sp, '✦ 스페셜 몬스터'); el('b', '', sp, c.special); }
    if (c.travel) { const tr = el('div', '', p, '⌂ 첫 클리어: ' + c.travel); tr.className = 'dh-sm-line'; tr.dataset.testid = 'stage-card-travel'; }
    this.cardScroll.scrollTop = 0;
    this.updateMore();
    // 입장 버튼(고정 발)
    this.foot.textContent = '';
    const go = el('button', '', this.foot, (c.locked ? '' : '⚔ ') + c.enterLabel); go.type = 'button';
    go.className = 'dh-sm-go' + (c.kind !== 'normal' ? ' boss' : '');
    go.dataset.testid = 'stage-map-enter';
    go.disabled = c.locked;
    go.addEventListener('click', (e) => { e.stopPropagation(); this.enter(); });
  }
}
